import type { SoundEntry } from '@shefira/shared';

import { ASSETS_BASE_URL, fetchManifest } from '../assets/manifest-loader.js';

/**
 * Every sound of the game goes through here (ADR 0019): the effects and the
 * music, one Web Audio context, one master switch.
 *
 * Plain Web Audio rather than Phaser's sound manager: the title screen plays
 * before Phaser exists, a zone change restarts the scene without cutting the
 * music, and the mute switch is one gain. Sounds are named by logical key
 * (`sfx.hit`, `music.forest`) from the manifest, never by file (rule 7).
 *
 * A browser only lets audio start after a gesture: until the first click or
 * key press the context waits, and the music asked for meanwhile starts then.
 * Any failure is silent — a sound is never worth an error.
 */

const MUTED_KEY = 'shefira.muted';
const SFX_VOLUME_KEY = 'shefira.sfx-volume';
const MUSIC_VOLUME_KEY = 'shefira.music-volume';
/**
 * Music sits under the effects: it is the room, not the event. These are the
 * mix; the player's two volumes (0..1, settings window) multiply them.
 */
const MUSIC_LEVEL = 0.32;
const SFX_LEVEL = 0.8;
const CROSSFADE_S = 2.5;
/** The same effect twice within this window plays once (a sale gives coins by two paths). */
const REPEAT_MS = 60;

let context: AudioContext | undefined;
let masterGain: GainNode | undefined;
let musicGain: GainNode | undefined;
let sfxGain: GainNode | undefined;
let sounds: Promise<Record<string, SoundEntry>> | undefined;
const buffers = new Map<string, Promise<AudioBuffer | null>>();
const lastPlayed = new Map<string, number>();
let muted = readMuted();
let sfxVolume = readVolume(SFX_VOLUME_KEY);
let musicVolume = readVolume(MUSIC_VOLUME_KEY);
const listeners = new Set<(muted: boolean) => void>();
const volumeListeners = new Set<() => void>();

/** The music wanted now, and the one actually sounding. */
let wantedMusic: string | undefined;
let playing: { key: string; source: AudioBufferSourceNode; gain: GainNode } | undefined;
/** The track being fetched, so a burst of calls starts it once. */
let loadingMusic: string | undefined;

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

/** A stored volume, 0..1; anything unreadable is full volume. */
function readVolume(storageKey: string): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return 1;
    const value = Number(raw);
    return Number.isFinite(value) ? clampVolume(value) : 1;
  } catch {
    return 1;
  }
}

function clampVolume(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function ensureContext(): AudioContext | undefined {
  if (context !== undefined) return context;
  try {
    context = new AudioContext();
    masterGain = context.createGain();
    masterGain.gain.value = muted ? 0 : 1;
    // A limiter at the end of the chain: a whirlwind's blows, a boss's slam
    // and a rare drop landing together would otherwise clip.
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    masterGain.connect(limiter).connect(context.destination);
    musicGain = context.createGain();
    musicGain.gain.value = MUSIC_LEVEL * musicVolume;
    musicGain.connect(masterGain);
    sfxGain = context.createGain();
    sfxGain.gain.value = SFX_LEVEL * sfxVolume;
    sfxGain.connect(masterGain);
  } catch {
    context = undefined;
  }
  return context;
}

/** The manifest's sounds, fetched once: the title screen comes before the boot scene. */
function soundTable(): Promise<Record<string, SoundEntry>> {
  sounds ??= fetchManifest()
    .then((manifest) => manifest.sounds)
    .catch(() => ({}));
  return sounds;
}

const MIME: Record<string, string> = {
  opus: 'audio/ogg; codecs="opus"',
  mp3: 'audio/mpeg',
};

/** The first of the sound's files this browser says it can play. */
function playableFile(entry: SoundEntry): string | undefined {
  const probe = document.createElement('audio');
  return entry.files.find((file) => {
    const type = MIME[file.slice(file.lastIndexOf('.') + 1)];
    return type !== undefined && probe.canPlayType(type) !== '';
  });
}

function buffer(key: string): Promise<AudioBuffer | null> {
  const known = buffers.get(key);
  if (known !== undefined) return known;
  const loading = (async () => {
    const audio = ensureContext();
    const entry = (await soundTable())[key];
    if (audio === undefined || entry === undefined) return null;
    const file = playableFile(entry);
    if (file === undefined) return null;
    const response = await fetch(ASSETS_BASE_URL + file);
    return audio.decodeAudioData(await response.arrayBuffer());
  })().catch(() => null);
  buffers.set(key, loading);
  return loading;
}

/** Warm the cache so the first swing of a fight is not late. */
export function preloadSounds(keys: readonly string[]): void {
  for (const key of keys) void buffer(key);
}

export interface SfxOptions {
  /** 0..1 on top of the effect's own level. */
  volume?: number;
  /** -1 left .. 1 right. */
  pan?: number;
  /** Playback rate: a hair of variation keeps a repeated hit from sounding mechanical. */
  rate?: number;
}

export function playSfx(key: string, options: SfxOptions = {}): void {
  const audio = ensureContext();
  if (audio === undefined || muted || audio.state !== 'running') return;
  const now = performance.now();
  if (now - (lastPlayed.get(key) ?? -Infinity) < REPEAT_MS) return;
  lastPlayed.set(key, now);
  void buffer(key).then((decoded) => {
    if (decoded === null || sfxGain === undefined) return;
    try {
      const source = audio.createBufferSource();
      source.buffer = decoded;
      source.playbackRate.value = options.rate ?? 1;
      const gain = audio.createGain();
      gain.gain.value = options.volume ?? 1;
      const panner = audio.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, options.pan ?? 0));
      source.connect(gain).connect(panner).connect(sfxGain);
      source.start();
    } catch {
      /* a sound is never worth an error */
    }
  });
}

/**
 * The music for where the player is. Asking for what already plays does
 * nothing, so the scene can call this as often as it likes; a change
 * crossfades, the old track fading while the new one rises.
 */
export function playMusic(key: string): void {
  if (wantedMusic === key) return;
  wantedMusic = key;
  void startWantedMusic();
}

async function startWantedMusic(): Promise<void> {
  const audio = ensureContext();
  const key = wantedMusic;
  if (audio === undefined || key === undefined || audio.state !== 'running') return;
  if (playing?.key === key || loadingMusic === key) return;
  loadingMusic = key;
  const decoded = await buffer(key);
  loadingMusic = undefined;
  // Asked for something else while this one loaded: that one wins.
  if (decoded === null || wantedMusic !== key || musicGain === undefined) return;
  try {
    const now = audio.currentTime;
    if (playing !== undefined) {
      const old = playing;
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0, now + CROSSFADE_S);
      old.source.stop(now + CROSSFADE_S + 0.1);
    }
    const source = audio.createBufferSource();
    source.buffer = decoded;
    source.loop = true;
    const gain = audio.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + CROSSFADE_S);
    source.connect(gain).connect(musicGain);
    source.start(now);
    playing = { key, source, gain };
  } catch {
    /* stay quiet */
  }
}

export function isMuted(): boolean {
  return muted;
}

/** Mute or unmute everything, remembered for the next visit (a UI preference). */
export function setMuted(value: boolean): void {
  muted = value;
  try {
    localStorage.setItem(MUTED_KEY, value ? '1' : '0');
  } catch {
    /* the switch still works for this visit */
  }
  const audio = ensureContext();
  if (audio !== undefined && masterGain !== undefined) {
    masterGain.gain.setTargetAtTime(value ? 0 : 1, audio.currentTime, 0.05);
  }
  for (const listener of listeners) listener(value);
}

export function onMutedChange(listener: (muted: boolean) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSfxVolume(): number {
  return sfxVolume;
}

export function getMusicVolume(): number {
  return musicVolume;
}

function storeVolume(storageKey: string, value: number): void {
  try {
    localStorage.setItem(storageKey, String(value));
  } catch {
    /* the volume still holds for this visit */
  }
}

/** The effects' volume, 0..1 over the mix, remembered for the next visit. */
export function setSfxVolume(value: number): void {
  sfxVolume = clampVolume(value);
  storeVolume(SFX_VOLUME_KEY, sfxVolume);
  const audio = ensureContext();
  if (audio !== undefined && sfxGain !== undefined) {
    sfxGain.gain.setTargetAtTime(SFX_LEVEL * sfxVolume, audio.currentTime, 0.02);
  }
  for (const listener of volumeListeners) listener();
}

/** The music's volume, 0..1 over the mix, remembered for the next visit. */
export function setMusicVolume(value: number): void {
  musicVolume = clampVolume(value);
  storeVolume(MUSIC_VOLUME_KEY, musicVolume);
  const audio = ensureContext();
  if (audio !== undefined && musicGain !== undefined) {
    musicGain.gain.setTargetAtTime(MUSIC_LEVEL * musicVolume, audio.currentTime, 0.02);
  }
  for (const listener of volumeListeners) listener();
}

/** Either volume moved (the settings window, or the account's saved settings). */
export function onVolumeChange(listener: () => void): () => void {
  volumeListeners.add(listener);
  return () => volumeListeners.delete(listener);
}

/**
 * Wake the context on the first gesture, and pause everything while the tab
 * is hidden: a background tab playing a village waltz is nobody's wish.
 */
export function installAudio(): void {
  const unlock = (): void => {
    const audio = ensureContext();
    if (audio === undefined) return;
    void audio.resume().then(() => startWantedMusic());
  };
  window.addEventListener('pointerdown', unlock, { capture: true });
  window.addEventListener('keydown', unlock, { capture: true });
  document.addEventListener('visibilitychange', () => {
    const audio = context;
    if (audio === undefined) return;
    if (document.hidden) void audio.suspend();
    else void audio.resume().then(() => startWantedMusic());
  });
}

/** What the audio is doing, for the browser console while developing. */
export function audioState(): Record<string, string | undefined> {
  return {
    context: context?.state,
    wanted: wantedMusic,
    playing: playing?.key,
    loading: loadingMusic,
  };
}
